import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Popover from "@mui/material/Popover";

import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import KMKTable from "../../Common/Table/KMKTable";
import ReportService from "../../../service/report";
import MultiSelectDropDown from "../../Common/MultiSelectDropDown";
import MultiSelect from "../../../util/multiselect";
import useWindowResize from "../../../hooks/useWindowsReszie";
import Workspace from "../../../service/workspace";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../store/actions";

const useStyles = makeStyles()((theme) => ({
  HeaderTitle: {
    fontSize: 20,
    display: "inline-flex",

    "& span": {
      marginLeft: "5px",
    },
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    width: "100%",
    overflow: "hidden",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
      wordBreak: "break-word",
    },
    "& .MuiTablePagination-toolbar": {
      minHeight: "40px",
    },
    "& P.MuiTablePagination-selectLabel,P.MuiTablePagination-displayedRows": {
      marginBottom: "0px !important",
    },
    "& ::-webkit-scrollbar-track": {
      marginLeft: "-2px",
    },
  },
}));

const reportColumns = [
  {
    id: "LoginId",
    name: "LOGIN ID",
    sortable: true,
    style: { maxWidth: 160, wordBreak: "break-word" },
    align: "left",
    width: 160,
    renderHeadCell: (name) => name,
    renderValueCell: (val, record, index) => val,
  },
  {
    id: "FirstName",
    name: "FIRST NAME",
    sortable: true,
    style: { maxWidth: 120 },
    align: "left",
    width: 120,
    renderHeadCell: (name) => name,
    renderValueCell: (val, record, index) => val,
  },
  {
    id: "LastName",
    name: "LAST NAME",
    sortable: true,
    style: { maxWidth: 120 },
    align: "left",
    width: 120,
    renderHeadCell: (name) => name,
    renderValueCell: (val, record, index) => val,
  },
  {
    id: "Status",
    name: "STATUS",
    sortable: true,
    style: { maxWidth: 120 },
    align: "left",
    width: 120,
    renderHeadCell: (name) => name,
    renderValueCell: (val, record, index) => (
      <>
        {val ? (
          <Chip
            icon={<CheckCircleOutlineIcon />}
            onClick={() => {}}
            label="Active"
            color="success"
            size="small"
            variant="outlined"
          />
        ) : (
          <Chip
            icon={<CancelOutlinedIcon />}
            onClick={() => {}}
            label="Inactive"
            color="error"
            size="small"
            variant="outlined"
          />
        )}
      </>
    ),
  },
  {
    id: "WorkspaceName",
    name: "WORKSPACE",
    style: { maxWidth: 140 },
    sortable: true,
    align: "left",
    width: 140,
    renderHeadCell: (name) => name,
    renderValueCell: (val, record, index) => val,
  },
  {
    id: "Permission",
    name: "ROLE",
    style: { maxWidth: 140 },
    sortable: true,
    align: "left",
    width: 140,
    renderHeadCell: (name) => name,
    renderValueCell: (val, record, index) => record.PermissionName,
  },
  {
    id: "WorkspaceOwner",
    name: "WORKSPACE OWNER",
    style: { maxWidth: 140 },
    sortable: true,
    align: "left",
    width: 140,
    renderHeadCell: (name) => name,
    renderValueCell: (val, record, index) => `${Boolean(val) ? "Yes" : "No"}`,
  },
];

const ROLES = [
  { val: 1, label: "Admin" },
  { val: 2, label: "Member" },
  { val: 3, label: "Visitor" },
];

const DetailTable = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const { height: tableHeight } = useWindowResize(220);

  const [loading, setLoading] = useState(false);
  const [workspaceAccessDetails, setWorkspaceAccessDetails] = useState([]);
  const [count, setCount] = useState(0);
  const [workspaceList, setWorkspaceList] = useState([]);
  const [search, setSearch] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const user = useSelector((state) => state.user);
  const { Roles: currentUserRoles } = useMemo(() => user?.currentUser || {}, [user]);
  const hasDownloadPermission = useMemo(
    () => ["SysAdmin", "BusinessAdmin"].some((role) => currentUserRoles.includes(role)),
    [currentUserRoles]
  );

  const onSuccess = useCallback(() => {
    dispatch(showToast({ message: "Workspace Access Report Exported Successfully", type: "success" }));
  }, [dispatch]);

  const options = useRef({
    limit: 25,
    page: 0,
    orderBy: "LoginId",
    order: "asc",
    workspaces: [],
    roles: [],
    search: "",
  });

  const fetchWorkspaceList = useCallback(async () => {
    const response = await new Workspace().getAllWorkspaces();
    if (response.success) {
      const allIds = response.data.map(({ Workspace_id }) => Workspace_id);
      options.current.workspaces = allIds;
      setWorkspaceList(response.data.map(({ Workspace_id, Name }) => ({ Name, label: Workspace_id })));
    }
  }, []);

  const getWorkspaceAccess = useCallback(async () => {
    const { order, orderBy, page, limit, workspaces, roles, search } = options.current;

    setLoading(true);

    const workspaceAccess = new ReportService();
    const { success, data } = await workspaceAccess.getWorkspaceAccess({
      order,
      orderBy,
      page,
      limit,
      search,
      ...(workspaces.length === workspaceList.length ? {} : { workspaceIds: workspaces.join(",") }),
      ...(roles.length === ROLES.length ? {} : { roleIds: roles.join(",") }),
    });

    if (success) {
      setWorkspaceAccessDetails(data.workspaceAccessDetails);
      setCount(data.count);
    }

    setLoading(false);
  }, [workspaceList.length]);

  const handleSorting = useCallback(
    (property) => {
      const isAsc = options.current.orderBy === property && options.current.order === "asc";

      options.current.orderBy = property;
      options.current.order = isAsc ? "desc" : "asc";

      getWorkspaceAccess();
    },
    [getWorkspaceAccess]
  );

  const handleChangePage = useCallback(
    (_event, newPage) => {
      options.current.page = newPage;

      getWorkspaceAccess();
    },
    [getWorkspaceAccess]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      options.current.limit = parseInt(event.target.value, 10);
      options.current.page = 0;

      getWorkspaceAccess();
    },
    [getWorkspaceAccess]
  );

  const handleWorkspaceChange = useCallback(
    (selectionList) => {
      options.current.workspaces = selectionList;

      getWorkspaceAccess();
    },
    [getWorkspaceAccess]
  );

  const handleRoleChange = useCallback(
    (selectionList) => {
      options.current.roles = selectionList;

      getWorkspaceAccess();
    },
    [getWorkspaceAccess]
  );

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      options.current.search = e.target.value;
      options.current.page = 0;

      getWorkspaceAccess();
    }
  };

  const handleClearSearch = () => {
    setSearch("");
    options.current.search = "";
    options.current.page = 0;

    getWorkspaceAccess();
  };

  const downloadReport = useCallback(async () => {
    const { order, orderBy, workspaces, roles, search } = options.current;
    setLoading(true);

    const workspaceAccess = new ReportService();
    await workspaceAccess.exportAccessReport(
      {
        order,
        orderBy,
        search,
        ...(workspaces.length === workspaceList.length ? {} : { workspaceIds: workspaces.join(",") }),
        ...(roles.length === ROLES.length ? {} : { roleIds: roles.join(",") }),
      },
      onSuccess
    );

    setLoading(false);
    handleMenuClose();
  }, [onSuccess, workspaceList.length]);

  const DownloadIcon = () => (
    <Box sx={{ minHeight: 35, minWidth: 35, mr: 2, pt: 1.2 }}>
      {hasDownloadPermission ? (
        <Tooltip title="Download Report" arrow>
          <IconButton
            color="primary"
            onClick={downloadReport}
            className={classes.IconHolder}
            disabled={loading || workspaceAccessDetails?.length === 0}
          >
            <DownloadRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <></>
      )}
    </Box>
  );

  const RefereshIcon = () => (
    <Box>
      <Tooltip placement="top" title="Refresh" arrow>
        <span>
          <IconButton disabled={loading} onClick={getWorkspaceAccess}>
            <RefreshOutlinedIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  const SearchField = () => (
    <TextField
      data-id="userListSearch"
      margin="normal"
      value={search}
      onKeyDown={handleSearch}
      onChange={(e) => setSearch(e.target.value)}
      placeholder="Search"
      variant="standard"
      InputProps={{
        startAdornment: <SearchIcon color="primary" />,
        ...(options.current.search && {
          endAdornment: (
            <IconButton onClick={handleClearSearch} size="small">
              <ClearIcon color="primary" />
            </IconButton>
          ),
        }),
      }}
      disabled={loading}
      sx={{ m: 0, mr: "20px" }}
    />
  );

  const RolesFilter = () => (
    <MultiSelectDropDown
      dataid="Roles"
      variant="standard"
      placeHolder={<MultiSelect title="Roles" dataLength={options.current.roles.length} />}
      options={ROLES}
      selected={options.current.roles}
      isSelectAll={true}
      disabled={loading}
      onSelectChange={handleRoleChange}
      basedOn="val"
      searchOn="label"
      allowSearching
    />
  );

  const WorkspaceFilter = () => (
    <MultiSelectDropDown
      dataid="workspaces"
      variant="standard"
      placeHolder={<MultiSelect title="Workspace" dataLength={options.current.workspaces.length} />}
      options={workspaceList}
      selected={options.current.workspaces}
      isSelectAll={true}
      disabled={loading}
      onSelectChange={handleWorkspaceChange}
      basedOn="label"
      searchOn="Name"
      allowSearching
    />
  );

  const DataTableToolbar = () => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography className={classes.HeaderTitle} component="div" noWrap>
          Workspace Access
        </Typography>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          {DownloadIcon()}
          {SearchField()}
          {RolesFilter()}
          {WorkspaceFilter()}
          {RefereshIcon()}
        </Box>

        <Box sx={{ display: { xs: "flex", md: "none" } }}>
          {SearchField()}

          <Box sx={{ minHeight: 35, minWidth: 35 }}>
            <IconButton onClick={handleMenuOpen} className={classes.IconHolder}>
              <MoreVertIcon />
            </IconButton>
          </Box>

          <Popover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            disableAutoFocus
            disableEnforceFocus
          >
            <Box sx={{ p: 2, width: "220px" }}>
              <Box sx={{ pb: 1 }}>{RolesFilter()}</Box>
              <Box sx={{ pb: 1 }}>{WorkspaceFilter()}</Box>
              <Divider />
              <Box sx={{ pt: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {RefereshIcon()}
                {DownloadIcon()}
              </Box>
            </Box>
          </Popover>
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    options.current.roles = ROLES.map(({ val }) => val);
    fetchWorkspaceList();
  }, [fetchWorkspaceList]);

  useEffect(() => {
    new Workspace().getAllWorkspaces().then((response) => {
      if (response.success) {
        options.current.workspaces = response.data.map(({ Workspace_id }) => Workspace_id);
        setWorkspaceList(response.data.map(({ Workspace_id, Name }) => ({ Name, label: Workspace_id })));
      }
    });
  }, []);

  useEffect(() => {
    getWorkspaceAccess();
  }, [getWorkspaceAccess]);

  return (
    <>
      {DataTableToolbar()}
      <Paper className={classes.DataTableContainer}>
        <KMKTable
          loading={loading}
          columns={reportColumns}
          containerHeight={tableHeight}
          noDataText={"No records found"}
          data={workspaceAccessDetails}
          sortingProperties={{
            order: options.current.order,
            orderBy: options.current.orderBy,
            handleSorting,
          }}
          boldHeader
          tableProps={{ stickyHeader: true }}
          pagination={true}
          paginationProperties={{
            rowsPerPageOptions: [25, 50, 100, 150],
            count,
            rowsPerPage: options.current.limit,
            page: options.current.page,
            onPageChange: handleChangePage,
            handleChangeRowsPerPage: handleChangeRowsPerPage,
            component: "div",
            className: classes.pagination,
            "data-id": "StewardQueueTablePagination",
          }}
          dataIdKey="SageMakerReportID"
        />
      </Paper>
    </>
  );
};

export default DetailTable;
